<div class="mt-6 w-full flex flex-col gap-5">
  <p-toast></p-toast>
  <p-confirmdialog />

  <p-card>
    <ng-template #title>
      <h1>
        {{
          isEditMode()
            ? ('content.announcements.edit' | translate)
            : ('content.announcements.add' | translate)
        }}
      </h1>
    </ng-template>

    <p class="text-sm text-color-secondary mb-4">
      {{ 'content.announcements.languageHint' | translate }}
    </p>

    <form
      [formGroup]="form"
      (ngSubmit)="onSubmit()"
      class="flex flex-col gap-4"
    >
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div class="flex flex-col">
          <label for="content-type" class="text-sm mb-1">
            {{ 'content.announcements.fields.type' | translate }}
          </label>
          <p-select
            inputId="content-type"
            formControlName="type"
            [options]="typeOptions"
            optionValue="value"
            appendTo="body"
          >
            <ng-template let-opt pTemplate="item">
              {{ opt.label | translate }}
            </ng-template>
            <ng-template let-opt pTemplate="selectedItem">
              {{ opt.label | translate }}
            </ng-template>
          </p-select>
        </div>

        <div class="flex flex-col">
          <label for="content-rank" class="text-sm mb-1">
            {{ 'content.announcements.fields.rank' | translate }}
          </label>
          <input
            id="content-rank"
            pInputText
            type="number"
            min="0"
            formControlName="rank"
          />
          <small class="text-color-secondary mt-1">
            {{ 'content.announcements.fields.rankHint' | translate }}
          </small>
          @if (form.get('rank')?.errors?.['server']) {
            <small class="text-red-500 mt-1">
              {{ form.get('rank')?.errors?.['server'].message }}
            </small>
          }
        </div>

        <div class="flex flex-col">
          <label for="content-from" class="text-sm mb-1">
            {{ 'content.announcements.fields.validFrom' | translate }}
          </label>
          <p-datepicker
            inputId="content-from"
            formControlName="valid_from"
            dateFormat="dd.mm.yy"
            [showTime]="true"
            [showIcon]="true"
            [showClear]="true"
            appendTo="body"
          />
          <small class="text-color-secondary mt-1">
            {{ 'content.announcements.fields.validFromHint' | translate }}
          </small>
        </div>

        <div class="flex flex-col">
          <label for="content-until" class="text-sm mb-1">
            {{ 'content.announcements.fields.validUntil' | translate }}
          </label>
          <p-datepicker
            inputId="content-until"
            formControlName="valid_until"
            dateFormat="dd.mm.yy"
            [showTime]="true"
            [showIcon]="true"
            [showClear]="true"
            appendTo="body"
          />
          <small class="text-color-secondary mt-1">
            {{ 'content.announcements.fields.validUntilHint' | translate }}
          </small>
          @if (form.get('valid_until')?.errors?.['server']) {
            <small class="text-red-500 mt-1">
              {{ form.get('valid_until')?.errors?.['server'].message }}
            </small>
          }
        </div>
      </div>

      <div class="flex flex-col">
        <label for="content-headline" class="text-sm mb-1">
          {{ 'content.announcements.fields.headline' | translate }}
        </label>
        <input
          id="content-headline"
          pInputText
          formControlName="headline"
          [maxlength]="255"
        />
      </div>

      <div class="flex flex-col">
        <label for="content-text" class="text-sm mb-1">
          {{ 'content.announcements.fields.text' | translate }}
        </label>
        <textarea
          id="content-text"
          pTextarea
          formControlName="text"
          rows="4"
        ></textarea>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div class="flex flex-col">
          <label for="content-cta-text" class="text-sm mb-1">
            {{ 'content.announcements.fields.ctaText' | translate }}
          </label>
          <input
            id="content-cta-text"
            pInputText
            formControlName="cta_text"
            [maxlength]="255"
          />
        </div>

        <div class="flex flex-col">
          <label for="content-cta-link" class="text-sm mb-1">
            {{ 'content.announcements.fields.ctaLink' | translate }}
          </label>
          <input
            id="content-cta-link"
            pInputText
            formControlName="cta_link"
            [maxlength]="255"
            placeholder="/tickets"
          />
          @if (form.get('cta_link')?.touched && form.get('cta_link')?.invalid) {
            <small class="text-red-500 mt-1">
              {{ 'content.announcements.validation.ctaLink' | translate }}
            </small>
          }
        </div>
      </div>

      <div class="flex justify-between items-center pt-2">
        <p-button
          type="button"
          severity="secondary"
          [label]="'label.cancel' | translate"
          (onClick)="onCancel()"
        />
        <div class="flex gap-2">
          @if (isEditMode() && model()) {
            <p-button
              type="button"
              severity="danger"
              icon="fa fa-trash"
              [label]="'label.delete' | translate"
              (onClick)="onDelete($event)"
            />
          }
          <p-button
            type="submit"
            [label]="'label.save' | translate"
            [loading]="submitting()"
          />
        </div>
      </div>
    </form>
  </p-card>

  @if (isEditMode() && model()) {
    <p-card>
      <ng-template #title>
        <h2>{{ 'content.announcements.imageSectionTitle' | translate }}</h2>
      </ng-template>

      <div class="flex flex-col gap-3">
        <p class="text-sm text-color-secondary">
          {{ 'content.announcements.imageHint' | translate }}
        </p>

        @if (image(); as media) {
          <div class="flex items-center gap-4">
            <img
              [src]="media.url"
              [alt]="media.name"
              class="max-h-32 rounded border border-surface-200"
            />
            <p-button
              type="button"
              severity="secondary"
              icon="fa fa-trash"
              [label]="'label.delete' | translate"
              (onClick)="onImageRemove()"
            />
          </div>
        }

        <p-fileupload
          mode="basic"
          accept="image/*"
          maxFileSize="5242880"
          [auto]="true"
          [customUpload]="true"
          (uploadHandler)="onImageSelected($event)"
          [chooseLabel]="'label.fileChoose' | translate"
        />
      </div>
    </p-card>
  }
</div>
